import { To } from 'react-router-dom'

import { useTranslation } from 'react-i18n-lite'

import {
  Button,
  LinkButton,
  Pagination,
  SortOption,
  SyntheticButton,
  YesObject
} from '@yes.technology/react-toolkit'

import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'

import { useSiteState } from 'siteState/shared'

import usePersistentSelection from '../../FilterFields/hooks/usePersistentSelection'
import useTableContext, { Mode } from '../hooks/useTableContext'

import DocumentActions from './DocumentActions'
import { Callbacks } from '../../EditValues/CancelModal/useCancelModal'
import { OBJECT_VIEW_NAVIGATION_CALLBACK_KEY } from '../../EditValues/useEditValues'
import useCancelChanges from '../../EditValues/CancelModal/useCancelChanges'
import { CustomActionButtonProps } from '../DataTables.types'

export type ActionsProps = {
  informational?: boolean
  idObjectClass: string
  object: YesObject
  pagination?: Pagination
  sortOptions?: SortOption[]
  objects?: YesObject[]
  sitemodel?: string
  target?: '_self' | '_blank'
  type?: 'view' | 'document' | 'sitemodel' | 'interaction-document' | null
  objectclassQueryString: string
  mode: Mode
  customActionButton?: CustomActionButtonProps
  isActiveObjectRow?: boolean
}

const Actions = ({
  idObjectClass,
  object,
  pagination,
  sortOptions,
  objects,
  type,
  sitemodel,
  target = '_self',
  objectclassQueryString,
  mode,
  customActionButton,
  isActiveObjectRow
}: ActionsProps) => {
  const [_, setObjectIndex] = useSiteState<number | number>('object-index')

  const [__, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  const [cancelModalCallbacks, setCancelModalCallbacks] = useSiteState<
    Record<string, Callbacks>
  >('cancel-modal-callbacks')

  const { disableLinkButton } = useTableContext()

  const { confirmIfPendingChanges } = useCancelChanges()

  const { t } = useTranslation()

  const navigateMergingState = useNavigateMergingState()

  const offset = pagination?.offset || 0

  const objectIndexInPage =
    objects?.findIndex((obj) => obj.uuid === object.uuid) || 0

  const objectIndex = offset + objectIndexInPage

  const { selectAndLink, shouldRenderSelectAndLink } = usePersistentSelection()

  const getVisualizeOption = (to: To) => ({
    children: t('data-tables.view'),
    onClick: () => {
      setObjectIndex(objectIndex)

      navigateMergingState(to, {
        state: {
          sortOptions,
          shouldFetchFromUuid: false
        }
      })

      setLockObjectViewObject(true)
    }
  })

  const selectAndLinkOption = {
    children: t('select-and-link.action'),
    onClick: () => selectAndLink([object])
  }

  const getOptions = (to: To) => [getVisualizeOption(to), selectAndLinkOption]

  const buildCustomActionUrl = (
    actionButton: CustomActionButtonProps
  ): string => {
    if (!actionButton.interpolation_fields?.length) {
      return actionButton.link.url
    }

    const url = new URL(actionButton.link.url, window.location.origin)

    for (const field of actionButton.interpolation_fields) {
      const value = object[field]
      if (value != null) {
        url.searchParams.set(field, String(value))
      }
    }

    return url.toString()
  }

  if (customActionButton) {
    return (
      <LinkButton
        variant='secondary'
        href={buildCustomActionUrl(customActionButton)}
        target={customActionButton.link.target}
        style={customActionButton.style}
      >
        {customActionButton.label}
      </LinkButton>
    )
  }

  if (type === 'document' || type === 'interaction-document') {
    return (
      <DocumentActions
        object={object}
        target={target}
        getOptions={getOptions}
        documentType={type}
      />
    )
  }

  if (type === 'sitemodel' && !!sitemodel) {
    return (
      <LinkButton
        variant='secondary'
        heightClass='small'
        disabled={mode !== 'view'}
        target={target}
        href={`/${sitemodel}?object-uuid=${object.uuid}&${objectclassQueryString}=${idObjectClass}`}
        style={{ padding: '1px 6px' }}
      >
        {t('data-tables.view')}
      </LinkButton>
    )
  }

  if (type === null) {
    return <></>
  }

  const path = {
    search: `?object-uuid=${object.uuid}&${objectclassQueryString}=${idObjectClass}`,
    hash: '#object-view'
  }

  const goToObject = () => {
    if (!Object.keys(cancelModalCallbacks).length) {
      setObjectIndex(objectIndex)
      setLockObjectViewObject(true)

      return
    }
    setCancelModalCallbacks((prevCallbacks) => ({
      ...prevCallbacks,
      [OBJECT_VIEW_NAVIGATION_CALLBACK_KEY]: {
        onSucceed: () => {
          setObjectIndex(objectIndex)
          setLockObjectViewObject(true)
        }
      }
    }))
  }

  const handleNavigation = () => {
    confirmIfPendingChanges(() => {
      const stateToMerge = {
        state: {
          sortOptions,
          shouldFetchFromUuid: false
        }
      }
      goToObject()
      navigateMergingState(path, stateToMerge)
    })
  }

  return (
    <>
      {!disableLinkButton && shouldRenderSelectAndLink() ? (
        <SyntheticButton
          options={getOptions(path)}
          heightClass='small'
          dropdownLabel='more'
        />
      ) : (
        <Button
          variant='secondary'
          heightClass='small'
          disabled={isActiveObjectRow || mode !== 'view'}
          onClick={handleNavigation}
        >
          {t('data-tables.view')}
        </Button>
      )}
    </>
  )
}

export default Actions
